import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { PageModel } from 'api/dto/Page.dto';
import { PageItemModel } from 'api/dto/PageItem.dto';
import { Resource } from 'model/Resource';
import {
  createAndListPageItems,
  createPage,
  deletePageItem,
  fetchPageList,
  getTranslatedPageItem,
  listPageItems,
  moveDownPage,
  moveUpPage,
  updateAndListPageItem,
} from 'reduxStore/page/asyncActions';
import { LangCodeEnum } from 'shared/enums/LangCodeEnum';
import { addAsyncCasesNew } from 'shared/utils/redux';

import { PageState, STORE_NAME } from './initialState';

export const pageSlice = createSlice({
  name: STORE_NAME,
  initialState: PageState.INITIAL_DOMAIN,
  reducers: {
    setCurrentItemByLanguage: (state, action: PayloadAction<PageItemModel>) => {
      state.currentItemByLanguage[action.payload.locale] = action.payload;
    },
    clearCurrentItemByLanguage: (state) => {
      state.currentItemByLanguage = PageState.INITIAL_DOMAIN.currentItemByLanguage;
    },
    updateLanguageTab: (state, action: PayloadAction<LangCodeEnum>) => {
      state.languageTab = action.payload;
    },
    updateQuestionDetailsTab: (state, action: PayloadAction<number>) => {
      state.questionDetailsTab = action.payload;
    },
    clearPageList: (state) => {
      state.listResource = PageState.INITIAL_DOMAIN.listResource;
    },
    setCurrentItem: (state, action: PayloadAction<PageItemModel>) => {
      state.currentItem = action.payload;
    },
    reorderPageItemLocally: (
      state,
      action: PayloadAction<{ newSourcePage: PageModel; newDestinationPage: PageModel }>,
    ) => {
      const { newSourcePage, newDestinationPage } = action.payload;
      const pageList = state.listResource.data;

      const updatedPageList = pageList.map((page) => {
        if (newDestinationPage.id === page.id) {
          return newDestinationPage;
        } else if (newSourcePage.id === page.id) {
          return newSourcePage;
        } else {
          return page;
        }
      });

      state.listResource = Resource.createResolvedResource({ data: updatedPageList });
    },
  },
  extraReducers: (builder) => {
    addAsyncCasesNew(builder, fetchPageList, (state, action) => {
      state.listResource = action.payload;
    });
    addAsyncCasesNew(builder, createPage, (state, action) => {
      state.listResource = action.payload;
    });
    addAsyncCasesNew(builder, moveUpPage, (state, action) => {
      state.listResource = action.payload;
    });
    addAsyncCasesNew(builder, moveDownPage, (state, action) => {
      state.listResource = action.payload;
    });

    const listPageItemsReducer = (
      state: Draft<PageState.Domain>,
      action: PayloadAction<PageItemModel[], string, { arg: { pageId: number } }>,
    ) => {
      const sortedPageItems = <PageItemModel[]>(
        action.payload.sort((a: PageItemModel, b: PageItemModel) => a.position - b.position)
      );

      state.listResource = getUpdatedListResourceByPageItems({
        listResource: state.listResource,
        pageItems: sortedPageItems,
        pageId: action.meta.arg.pageId,
      });
    };
    builder.addCase(listPageItems.fulfilled, listPageItemsReducer);
    builder.addCase(createAndListPageItems.fulfilled, listPageItemsReducer);
    builder.addCase(updateAndListPageItem.fulfilled, listPageItemsReducer);
    builder.addCase(deletePageItem.fulfilled, listPageItemsReducer);

    builder.addCase(getTranslatedPageItem.fulfilled, (state, action) => {
      state.currentItemByLanguage[action.meta.arg.language] = action.payload;
    });
    builder.addCase(getTranslatedPageItem.rejected, (state, action) => {
      state.currentItemByLanguage[action.meta.arg.language] = null;
    });
  },
});

const getUpdatedListResourceByPageItems = ({
  listResource,
  pageItems,
  pageId,
}: {
  listResource: Draft<Resource<PageModel[]>>;
  pageItems: PageItemModel[];
  pageId: number;
}): Resource<PageModel[]> => {
  const pageList = listResource.data;

  const updatedPageList = pageList.map((page) =>
    page.id === pageId ? { ...page, items: pageItems } : page,
  );

  return Resource.createResolvedResource({ data: updatedPageList as PageModel[] });
};

export const {
  setCurrentItemByLanguage,
  clearCurrentItemByLanguage,
  clearPageList,
  setCurrentItem,
  reorderPageItemLocally,
  updateLanguageTab,
  updateQuestionDetailsTab,
} = pageSlice.actions;
